/**
 * Billing Module — permissions derived from Cognito groups.
 *
 * The legacy app stored boolean flags (isBP, isBA, isAM, isAdminFeatureAllowed)
 * directly on `state.user`. In CIP we derive them on demand from
 * `state.auth.user.groups` so the auth slice doesn't carry billing-specific
 * concerns.
 *
 * For React components, prefer the `useBillingPermissions` hook in
 * `src/hooks/useBillingPermissions.ts` — it memoises the result against the
 * groups reference. This file exports the pure derivation only so it can be
 * called from hexagon services, sagas, and tests.
 *
 * `isAdminFeatureAllowed` gates admin-only sections in the
 * MonthlyReportModal (Patient Flags, Patient Visits, Escalation Reports)
 * and is sourced from the `admin-features` Cognito group — same name the
 * legacy app used.
 */

import type { BillingPermissions } from "../hexagon/core/ports/BillingModule";

const BILLING_PRACTITIONER: UserGroup = "MedicalGroup-BillingPractitioner";
const ADMINISTRATOR: UserGroup = "MedicalGroup-Administrator";
const RCM: UserGroup = "RCM";
const ACCOUNT_MANAGER: UserGroup = "AccountManager";
const ADMIN_FEATURES: UserGroup = "admin-features";
const AUDIT_REPORT_EXPORT: UserGroup = "audit_report_export";

export const deriveBillingPermissions = (
  groups: UserGroup[] | undefined | null
): BillingPermissions => {
  const groupSet = new Set(groups ?? []);
  return {
    isBP: groupSet.has(BILLING_PRACTITIONER),
    isBA: groupSet.has(ADMINISTRATOR),
    isRCM: groupSet.has(RCM),
    isAM: groupSet.has(ACCOUNT_MANAGER),
    isAdminFeatureAllowed: groupSet.has(ADMIN_FEATURES),
    isAuditReportExport: groupSet.has(AUDIT_REPORT_EXPORT),
  };
};
